import { useMemo } from 'react'
import { useScopeStore } from './store'
import { useSettingsStore } from '../settings/store'
import { calculateAutoHours, calculateTotalHours } from './hourCalculator'
import { COMPLEXITY_OPTIONS } from './types'

/**
 * Hook to get total scope hours from auto-generated + manual entries
 */
export function useScopeHours() {
  const { scope } = useScopeStore()
  const { hourSettings } = useSettingsStore()

  return useMemo(() => {
    // Auto-generated hours from scope configuration
    const autoHours = calculateAutoHours(scope, hourSettings)
    const autoTotal = calculateTotalHours(autoHours)

    // Manual hours with complexity multipliers
    const manualTotal = scope.scopeHours.reduce((sum, h) => {
      const multiplier = COMPLEXITY_OPTIONS.find((c) => c.id === h.complexity)?.multiplier ?? 1
      return sum + h.estimatedHours * multiplier
    }, 0)

    // Feature request hours
    const featureRequestTotal = scope.featureRequests.reduce((sum, r) => sum + r.hours, 0)

    const totalHours = autoTotal + manualTotal + featureRequestTotal

    return {
      autoHours: autoTotal,
      manualHours: manualTotal,
      featureRequestHours: featureRequestTotal,
      totalHours,
      autoEntries: autoHours,
    }
  }, [scope, hourSettings])
}

/**
 * Calculate implementation fee from scope hours
 */
export function useScopeImplementationFee(hourlyRate: number = 250) {
  const { totalHours } = useScopeHours()
  return totalHours * hourlyRate
}
